import { AutoComplete } from '@nineeng/form-components';
import React, { useState, useEffect } from 'react';
import isEmail from 'validator/lib/isEmail';
import { TextField } from '@mui/material';

const MultiEmailInput = ({
	onChange,
	value,
	placeholder,
	error = false,
	helperText = undefined,
	label = undefined,
	required = false,
	...props
}) => {
	// Maintain local state to preserve value when prop is undefined (e.g., in repeater temporary state)
	const [localValue, setLocalValue] = useState(value || []);

	// Update local value when prop changes (but only if prop is defined)
	// Also update if value is an empty array (to clear the field)
	useEffect(() => {
		if (value !== undefined) {
			setLocalValue(value);
		}
	}, [value]);

	const handleChange = ({ target }) => {
		const processedValue = (target.value || [])
			.map((v) => {
				if (typeof v === 'string') {
					return v.trim();
				} else {
					return v.id?.trim();
				}
			})
			.filter((v) => v && isEmail(v))
			.map((v) => ({ id: v.toLowerCase(), inputValue: v.toLowerCase() }));

		// Update local state immediately
		setLocalValue(processedValue);

		// Notify parent
		onChange({ target: { ...target, value: processedValue, name: target.name || 'emails' } });
	};

	// Always use a defined value - use localValue when prop is undefined
	// This ensures the repeater can read the current value even when prop is undefined
	const currentValue = value !== undefined ? value : localValue;

	return (
		<AutoComplete
			{...props}
			value={currentValue}
			multiple
			options={[]}
			autoComplete={false}
			handleHomeEndKeys
			freeSolo
			clearOnBlur
			onChange={handleChange}
			noOptionsText="Please provide a valid email address"
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					placeholder={currentValue && currentValue.length > 0 ? '' : placeholder || 'Enter email addresses'}
					error={!!error}
					helperText={helperText}
					sx={{
						...params.sx,
						'& .MuiInputBase-input::placeholder': {
							opacity: 1,
							color: 'text.secondary',
						},
					}}
				/>
			)}
			getOptionLabel={(option) => {
				// handle enter key
				if (typeof option === 'string') {
					return option;
				}
				return option?.id;
			}}
			renderOption={(props, option) => {
				return (
					<li {...props} key={option?.id || option?.inputValue}>
						{option?.name}
					</li>
				);
			}}
			filterOptions={(options, params) => {
				const filtered = [...options];
				const { inputValue } = params;

				if (inputValue && isEmail(inputValue)) {
					filtered.push({
						id: inputValue,
						inputValue,
						name: `Add "${inputValue}"`,
					});
				}

				return filtered;
			}}
		/>
	);
};

export default MultiEmailInput;
